import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { useAuth } from '../lib/AuthContext'
import { getNotifications, markNotificationsRead } from '../lib/supabase'
import './Notifications.css'

function getInitials(name) {
  return name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?'
}

function clip(text, max = 120) {
  if (!text) return ''
  return text.length > max ? `${text.slice(0, max)}…` : text
}

function buildNotificationCopy(notification) {
  const actor = notification.actor_profile?.display_name
    || notification.actor_profile?.username
    || 'Someone'

  const capsulePreview = notification.metadata?.capsule_preview
    || notification.capsule?.content
    || ''

  switch (notification.type) {
    case 'new_follower':
      return {
        title: `${actor} followed you`,
        body: notification.actor_profile?.username ? `@${notification.actor_profile.username} is now following your profile.` : 'They are now following your profile.'
      }
    case 'capsule_like':
      return {
        title: `${actor} liked your capsule`,
        body: capsulePreview ? `“${clip(capsulePreview)}”` : 'One of your capsules received a like.'
      }
    case 'capsule_dislike':
      return {
        title: `${actor} disliked your capsule`,
        body: capsulePreview ? `“${clip(capsulePreview)}”` : 'One of your capsules received a dislike.'
      }
    case 'new_response':
      return {
        title: `${actor} responded to your capsule`,
        body: notification.metadata?.response_preview
          ? `Response: “${clip(notification.metadata.response_preview)}”`
          : (capsulePreview ? `On: “${clip(capsulePreview)}”` : 'You received a new response.')
      }
    default:
      return {
        title: 'New activity',
        body: 'You have a new notification.'
      }
  }
}

function getNotificationDestination(notification) {
  if (notification.type === 'new_follower' && notification.actor_profile?.username) {
    return `/user/${notification.actor_profile.username}`
  }

  if (notification.capsule_id) {
    if (notification.type === 'new_response') {
      return `/capsule/${notification.capsule_id}?view=responses`
    }
    return `/capsule/${notification.capsule_id}`
  }

  if (notification.actor_profile?.username) {
    return `/user/${notification.actor_profile.username}`
  }

  return null
}

export default function Notifications() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!user?.id) return
    let cancelled = false

    const load = async () => {
      setLoading(true)
      setError('')
      try {
        const items = await getNotifications(user.id, 80)
        if (cancelled) return
        setNotifications(items)

        await markNotificationsRead(user.id)
        if (cancelled) return
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })))
      } catch (e) {
        if (cancelled) return
        console.error('Failed to load notifications:', e)
        setError(e.message || 'Could not load notifications')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => { cancelled = true }
  }, [user?.id])

  return (
    <div className="notifications-page">
      <header className="notifications-header">
        <button className="notifications-back-btn" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <div className="notifications-header-title-wrap">
          <div className="notifications-header-title">Notifications</div>
          <div className="notifications-header-subtitle">Followers, capsule reactions, and new responses</div>
        </div>
      </header>

      <main className="notifications-body">
        {loading && (
          <div className="notifications-loading"><span className="spinner" /></div>
        )}

        {!loading && error && (
          <div className="notifications-empty">
            <div className="notifications-empty-glyph">◇</div>
            <div>{error}</div>
          </div>
        )}

        {!loading && !error && notifications.length === 0 && (
          <div className="notifications-empty">
            <div className="notifications-empty-glyph">◇</div>
            <div className="notifications-empty-title">No notifications yet</div>
            <div className="notifications-empty-body">
              New followers, likes, dislikes, and responses will appear here.
            </div>
          </div>
        )}

        {!loading && !error && notifications.length > 0 && (
          <div className="notifications-list">
            {notifications.map(notification => {
              const actor = notification.actor_profile
              const copy = buildNotificationCopy(notification)
              const destination = getNotificationDestination(notification)
              const timeAgo = notification.created_at
                ? formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })
                : ''

              return (
                <div
                  key={notification.id}
                  className={`notification-card ${notification.is_read ? '' : 'unread'}`}
                  role={destination ? 'button' : undefined}
                  tabIndex={destination ? 0 : undefined}
                  onClick={destination ? () => navigate(destination) : undefined}
                  onKeyDown={destination ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      navigate(destination)
                    }
                  } : undefined}
                >
                  <div
                    className="notification-avatar"
                    style={{ background: actor?.avatar_color || '#152550' }}
                  >
                    {actor?.avatar_url ? (
                      <img src={actor.avatar_url} alt="" className="notification-avatar-image" />
                    ) : (
                      getInitials(actor?.display_name || actor?.username)
                    )}
                  </div>

                  <div className="notification-content">
                    <div className="notification-title-row">
                      <div className="notification-title">{copy.title}</div>
                      {timeAgo && <div className="notification-time mono">{timeAgo}</div>}
                    </div>
                    <div className="notification-body-text">{copy.body}</div>
                    {actor?.username && (
                      <button
                        type="button"
                        className="notification-link-btn"
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(`/user/${actor.username}`)
                        }}
                      >
                        View @{actor.username}
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </main>
    </div>
  )
}
